import { MS365Document } from "../types/types";

export const normalizeTimelineData = (
  emails: any[],
  events: any[],
  documents?: any[],
  tasks: any[] = [],
  copilot: any[] = []
): MS365Document[] => {
  copilot = Array.isArray(copilot) ? copilot : [];

  const emailEvents = emails.map((email) => ({
    id: email.id,
    type: "email" as const,
    title: email.subject.replace(/_{3,}/g, "…"),
    timestamp: email.receivedDateTime,
    summary:
      typeof email.bodyPreview === "string"
        ? email.bodyPreview.replace(/_{3,}/g, "…").slice(0, 200) +
          (email.bodyPreview.length >= 200 ? "…" : "")
        : email.bodyPreview,
    link: email.webLink,
    metadata: {},
    hasAttachments: email.hasAttachments || false,
    attachments: email.attachments || [],
    relatedIds: Array.isArray(email.attachments)
      ? email.attachments.map((att: any) => att.id)
      : [],
    conversationId: email.conversationId,
    body: email.body,
  }));

  const meetingEvents = events.map((event) => ({
    id: event.id,
    type: "event" as const,
    title: event.subject,
    timestamp: event.start.dateTime,
    summary:
      typeof event.bodyPreview === "string"
        ? event.bodyPreview.replace(/_{3,}/g, "…").slice(0, 200) +
          (event.bodyPreview.length >= 200 ? "…" : "")
        : event.bodyPreview,
    link: event.webLink,
    metadata: {},
    hasAttachments: event.hasAttachments || false,
    attachments: event.attachments || [],
    relatedIds: Array.isArray(event.attachments)
      ? event.attachments.map((att: any) => att.id)
      : [],
    body: event.body,
  }));

  const documentEvents = (documents || []).map((doc) => {
    return {
      id: doc.id,
      type:
        doc.driveType === "onedrive" || doc.driveType === "sharepoint"
          ? ("sharepoint" as const)
          : ("genericfile" as const),
      title: doc.name,
      timestamp: doc.lastModifiedDateTime || doc.createdDateTime,
      summary: undefined,
      link: doc.webUrl,
      metadata: {},
      hasAttachments: false,
      driveType: (doc.driveType === "onedrive"
        ? "onedrive"
        : doc.driveType === "sharepoint"
        ? "sharepoint"
        : "sharepoint") as "onedrive" | "sharepoint",
      fileType: doc.file?.mimeType || undefined,
      fileSize: doc.size,
      lastModifiedBy: doc.lastModifiedBy?.user?.displayName || undefined,
      documentLocation:
        doc.driveType === "onedrive"
          ? "OneDrive"
          : doc.driveType === "sharepoint"
          ? "SharePoint"
          : undefined,
      documentPath: doc.parentReference?.path || undefined,
      previewUrl: undefined, // Can be set if you have a preview link
      siteId: doc.siteId,
      driveId: doc.parentReference?.driveId || undefined,
      webUrl: doc.webUrl,
      thumbnails: doc.thumbnails || [],
      objectWidth: doc.objectWidth || 100,
      objectHeight: doc.objectHeight || 100,
      relatedIds: Array.isArray(doc.relatedIds) ? doc.relatedIds : [],
    };
  });

  const copilotResponses = copilot.map((doc) => {
    try {
      return {
        id: doc.id,
        type: "genericfile" as const,
        title: doc.resourceMetadata.Title || "Copilot Response",
        timestamp:
          doc.resourceMetadata.LastModifiedTime || new Date().toISOString(),
        summary: doc.extracts[0].text || "",
        link: doc.webUrl || "",
        metadata: doc.resourceMetadata || {},
        hasAttachments: false,
        driveType: "sharepoint" as "onedrive" | "sharepoint", // fallback to a valid MS365Document driveType
        fileType: doc.resourceMetadata.FileType || undefined,
        fileSize: doc.fileSize || 0,
        lastModifiedBy: doc.resourceMetadata.ModifiedBy || undefined,
        documentLocation: "Copilot",
        documentPath: doc.resourceMetadata?.Path || "",
        previewUrl: doc.previewUrl || "",
        siteId: doc.resourceMetadata.SiteID || "",
        driveId: "",
        webUrl: doc.webUrl || "",
        thumbnails: [],
        objectWidth: 100,
        objectHeight: 100,
        relatedIds: [],
      };
    } catch (error) {
      console.error("Error processing Copilot response:", error);
      return {
        id: "error",
        type: "genericfile" as const,
        title: "Error",
        timestamp: new Date().toISOString(),
        summary: "Error processing Copilot response",
        link: "",
        metadata: {},
        hasAttachments: false,
        driveType: "sharepoint" as "onedrive" | "sharepoint",
        fileType: undefined,
        fileSize: 0,
        lastModifiedBy: undefined,
        documentLocation: "Copilot",
        documentPath: "",
        previewUrl: "",
        siteId: "",
        driveId: "",
        webUrl: "",
        thumbnails: [],
        objectWidth: 100,
        objectHeight: 100,
        relatedIds: [],
      };
    }
  });

  const taskEvents = tasks.map((task) => ({
    id: task.id,
    type: "todo" as const,
    title: task.title,
    timestamp: task.lastModifiedDateTime || task.createdDateTime,
    summary: task.bodyPreview,
    link: task.webLink,
    metadata: {},
    hasAttachments: task.hasAttachments || false,
    attachments: task.attachments || [],
    relatedIds: Array.isArray(task.attachments)
      ? task.attachments.map((att: any) => att.id)
      : [],
    body: task.body,
  }));

  return [
    ...emailEvents,
    ...meetingEvents,
    ...documentEvents,
    ...taskEvents,
    ...copilotResponses,
  ].sort(
    (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
  );
};

export const getExtensionFromString = (item: MS365Document) => {
  // Extract file extension from the title (filename)
  let itemType: string;
  switch (item.type) {
    case "sharepoint":
    case "genericfile":
      const match = item.title.match(/\.([a-zA-Z0-9]+)$/);
      if (match && match[1]) {
        switch (match[1].toLowerCase()) {
          case "mp4":
            itemType = "genericfile";
            break;
          case "url":
            itemType = "genericfile";
            break;
          default:
            itemType = match[1].toLowerCase();
            break;
        }
      } else {
        itemType = "genericfile";
      }
      break;
    case "event":
      itemType = "calendar";
      break;
    case "email":
      itemType = "email";
      break;
    case "chat":
      itemType = "chat";
      break;
    default:
      itemType = "genericfile";
      break;
  }

  // Default to 'genericfile' if no extension found
  return itemType || "genericfile";
};

export const getExtension = (item: MS365Document) => {
  // Extract file extension from the title (filename)
  let itemType: string;
  const match = item.title.match(/\.([a-zA-Z0-9]+)$/);
  if (match && match[1]) {
    itemType = match[1].toLowerCase();
  }

  // Default to 'genericfile' if no extension found
  return itemType || "genericfile";
};
